export const PIPELINE_ATTRIBUTE_KEY = "etapa_comercial";
export const PIPELINE_ATTRIBUTE_PREFIX = "avant_pipeline_";
export const FLOW_METADATA_PREFIX = "AVANT_FLOW_V1:";

export const STAGE_COLORS = [
  "blue",
  "amber",
  "violet",
  "teal",
  "green",
  "ruby",
  "slate",
];

const DEFAULT_STAGE_NAMES = [
  "Novo lead",
  "Qualificação",
  "Reunião agendada",
  "Proposta enviada",
  "Fechado — ganho",
  "Fechado — perdido",
];

const slugify = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 42);

export const stageId = (name, index = 0) =>
  `${slugify(name) || "etapa"}_${index + 1}`;

export const defaultStages = () =>
  DEFAULT_STAGE_NAMES.map((name, index) => ({
    id: stageId(name, index),
    name,
    color: STAGE_COLORS[index % STAGE_COLORS.length],
    type:
      index === DEFAULT_STAGE_NAMES.length - 2
        ? "won"
        : index === DEFAULT_STAGE_NAMES.length - 1
          ? "lost"
          : "open",
  }));

export const parseFlowMetadata = (attribute) => {
  const values =
    attribute?.attributeValues || attribute?.attribute_values || [];
  const description =
    attribute?.attributeDescription || attribute?.attribute_description || "";
  let stored = {};

  if (description.startsWith(FLOW_METADATA_PREFIX)) {
    try {
      stored = JSON.parse(description.slice(FLOW_METADATA_PREFIX.length));
    } catch {
      stored = {};
    }
  }

  const storedStages = Array.isArray(stored.stages) ? stored.stages : [];
  return {
    version: 1,
    description: stored.description || "",
    inboxIds: Array.isArray(stored.inboxIds)
      ? stored.inboxIds.map(Number).filter(Number.isFinite)
      : [],
    compact: Boolean(stored.compact),
    stages: values.map((name, index) => {
      const matched =
        storedStages.find((stage) => stage.name === name) ||
        storedStages[index] ||
        {};
      return {
        id: matched.id || stageId(name, index),
        name,
        color: STAGE_COLORS.includes(matched.color)
          ? matched.color
          : STAGE_COLORS[index % STAGE_COLORS.length],
        type: ["open", "won", "lost"].includes(matched.type)
          ? matched.type
          : /ganh|won|fechad.*sucesso/i.test(name)
            ? "won"
            : /perdid|lost|cancelad/i.test(name)
              ? "lost"
              : "open",
        description: matched.description || "",
      };
    }),
  };
};

export const serializeFlowMetadata = (metadata) =>
  `${FLOW_METADATA_PREFIX}${JSON.stringify({
    version: 1,
    description: metadata.description || "",
    inboxIds: metadata.inboxIds || [],
    compact: Boolean(metadata.compact),
    stages: metadata.stages || [],
  })}`;

export const isPipelineAttribute = (attribute) => {
  const key = attribute?.attributeKey || attribute?.attribute_key || "";
  const description =
    attribute?.attributeDescription || attribute?.attribute_description || "";
  return (
    key === PIPELINE_ATTRIBUTE_KEY ||
    key.startsWith(PIPELINE_ATTRIBUTE_PREFIX) ||
    description.startsWith(FLOW_METADATA_PREFIX)
  );
};

export const pipelineBoards = (attributes) =>
  (attributes || []).filter(isPipelineAttribute).map((attribute) => ({
    ...attribute,
    key: attribute.attributeKey || attribute.attribute_key,
    name:
      attribute.attributeDisplayName ||
      attribute.attribute_display_name ||
      "Funil sem nome",
    metadata: parseFlowMetadata(attribute),
  }));

export const buildPipelineKey = (name, existingKeys = []) => {
  const base = `${PIPELINE_ATTRIBUTE_PREFIX}${slugify(name) || "funil"}`;
  let key = base;
  let suffix = 2;
  while (existingKeys.includes(key)) {
    key = `${base}_${suffix}`;
    suffix += 1;
  }
  return key;
};

export const normalizeConversation = (conversation) => {
  const sender = conversation?.meta?.sender || conversation?.contact || {};
  const lastMessage =
    conversation?.last_non_activity_message ||
    conversation?.messages?.at?.(-1) ||
    {};

  return {
    ...conversation,
    sender,
    contactName:
      sender.name || sender.phone_number || `Contato #${sender.id || ""}`,
    phoneNumber: sender.phone_number || "",
    avatarUrl: sender.thumbnail || "",
    inboxName: conversation?.inbox?.name || "",
    assigneeName: conversation?.meta?.assignee?.name || "",
    assigneeId: conversation?.meta?.assignee?.id || null,
    preview: lastMessage.content || "",
    priority: conversation?.priority || null,
    labels: Array.isArray(conversation?.labels) ? conversation.labels : [],
    custom_attributes: conversation?.custom_attributes || {},
  };
};

export const payloadConversations = (response) => {
  const data = response?.data || response || {};
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.payload)) return data.payload;
  if (Array.isArray(data.data?.payload)) return data.data.payload;
  return [];
};

export const matchesKanbanSearch = (conversation, query) => {
  const normalized = String(query || "")
    .trim()
    .toLocaleLowerCase("pt-BR");
  if (!normalized) return true;
  return [
    conversation.contactName,
    conversation.phoneNumber,
    conversation.assigneeName,
    conversation.inboxName,
    conversation.preview,
    ...(conversation.labels || []),
    conversation.display_id,
  ].some((value) =>
    String(value || "")
      .toLocaleLowerCase("pt-BR")
      .includes(normalized),
  );
};

export const matchesKanbanFilters = (
  conversation,
  { inboxId = "all", assigneeId = "all", priority = "all" } = {},
) => {
  const inboxMatches =
    inboxId === "all" || Number(conversation.inbox_id) === Number(inboxId);
  const assigneeMatches =
    assigneeId === "all" ||
    (assigneeId === "unassigned"
      ? !conversation.assigneeId
      : Number(conversation.assigneeId) === Number(assigneeId));
  const priorityMatches =
    priority === "all" ||
    (priority === "none"
      ? !conversation.priority
      : conversation.priority === priority);

  return inboxMatches && assigneeMatches && priorityMatches;
};

const PRIORITY_WEIGHT = { urgent: 4, high: 3, medium: 2, low: 1 };

export const sortKanbanConversations = (conversations, sortBy = "recent") => {
  const sorted = [...conversations];
  sorted.sort((left, right) => {
    if (sortBy === "oldest") {
      return (
        Number(left.last_activity_at || 0) - Number(right.last_activity_at || 0)
      );
    }
    if (sortBy === "priority") {
      const priorityDifference =
        (PRIORITY_WEIGHT[right.priority] || 0) -
        (PRIORITY_WEIGHT[left.priority] || 0);
      if (priorityDifference) return priorityDifference;
    }
    return (
      Number(right.last_activity_at || 0) - Number(left.last_activity_at || 0)
    );
  });
  return sorted;
};

export const moveConversationBetweenColumns = (
  columns,
  conversationId,
  targetStage,
) => {
  let moved;
  const next = columns.map((column) => ({
    ...column,
    conversations: column.conversations.filter((conversation) => {
      if (Number(conversation.id) !== Number(conversationId)) return true;
      moved = conversation;
      return false;
    }),
  }));

  if (!moved) return columns;
  return next.map((column) =>
    column.stage === targetStage
      ? { ...column, conversations: [moved, ...column.conversations] }
      : column,
  );
};
